import lottieThrowFilenamesJson from "./generated/lottieThrowFilenames.json";

/** CRA `public/` base (e.g. `/subdir` when hosted under a path) */
function lottiePublicPath(relativePath: string): string {
  const raw =
    typeof process !== "undefined" && process.env.PUBLIC_URL != null
      ? String(process.env.PUBLIC_URL)
      : "";
  const base = raw.replace(/\/$/, "");
  const path = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
  return base ? `${base}${path}` : path;
}

/** Build URLs for JSON files under `public/lottie/<dir>/` (encode spaces etc. in filenames). */
function lottieDirSrcs(dir: string, basenames: readonly string[]): string[] {
  return basenames.map((name) => lottiePublicPath(`/lottie/${dir}/${encodeURIComponent(name)}`));
}

/** Wet paint / splatter — legacy lab asset only (`public/lottie/legacy/`) */
export const WET_PAINT_SPLAT_JSON = lottiePublicPath("/lottie/legacy/wet-paint-splat.json");

/**
 * Throw pools are generated from disk (`public/lottie/<kind>/*.json`) by
 * `scripts/generate-lottie-throw-manifests.mjs` → `src/generated/lottieThrowFilenames.json`.
 * Random picks use index `Math.floor(Math.random() * urls.length)` in `pickRandomThrowSrc`.
 */
type LottieThrowManifest = {
  tomato: string[];
  poop: string[];
  kiss: string[];
  egg: string[];
  punch: string[];
  love: string[];
  brokenHeart: string[];
  dancing: string[];
  highFive: string[];
  angry: string[];
  facepalm: string[];
  slipper: string[];
};

const lottieThrowFilenames = lottieThrowFilenamesJson as LottieThrowManifest;

function throwPoolOrThrow(kind: keyof LottieThrowManifest): string[] {
  const names = lottieThrowFilenames[kind];
  if (!names.length) {
    throw new Error(
      `[reactionThrowAssets] No Lottie JSON for reaction pool "${kind}". Run: npm run generate:lottie-manifests`
    );
  }
  return names;
}

export const TOMATO_THROW_LOTTIE_SRCS = lottieDirSrcs("tomato", throwPoolOrThrow("tomato"));
export const POOP_THROW_LOTTIE_SRCS = lottieDirSrcs("poop", throwPoolOrThrow("poop"));
export const KISS_THROW_LOTTIE_SRCS = lottieDirSrcs("kiss", throwPoolOrThrow("kiss"));
export const EGG_THROW_LOTTIE_SRCS = lottieDirSrcs("egg", throwPoolOrThrow("egg"));
export const PUNCH_THROW_LOTTIE_SRCS = lottieDirSrcs("punch", throwPoolOrThrow("punch"));
export const LOVE_THROW_LOTTIE_SRCS = lottieDirSrcs("love", throwPoolOrThrow("love"));
export const BROKEN_HEART_THROW_LOTTIE_SRCS = lottieDirSrcs("broken-heart", throwPoolOrThrow("brokenHeart"));
export const DANCING_THROW_LOTTIE_SRCS = lottieDirSrcs("dancing", throwPoolOrThrow("dancing"));
export const HIGH_FIVE_THROW_LOTTIE_SRCS = lottieDirSrcs("high-five", throwPoolOrThrow("highFive"));
export const ANGRY_THROW_LOTTIE_SRCS = lottieDirSrcs("angry", throwPoolOrThrow("angry"));
export const FACEPALM_THROW_LOTTIE_SRCS = lottieDirSrcs("facepalm", throwPoolOrThrow("facepalm"));
export const SLIPPER_THROW_LOTTIE_SRCS = lottieDirSrcs("slipper", throwPoolOrThrow("slipper"));

/** Fallback / default Lottie URL; tomato overlay uses `pickStableTomatoThrowSrc` from the scanned pool. */
export const TOMATO_THROW_JSON = lottiePublicPath("/lottie/tomato/tomato-throw.json");
export const PRESENT_POOP_JSON = lottiePublicPath("/lottie/poop/present-poop.json");
export const POOP_RESEARCH_JSON = lottiePublicPath("/lottie/poop/poop-research.json");

export const KISS_HEART_JSON = lottiePublicPath("/lottie/kiss/kiss-heart.json");
export const MOUTH_JSON = lottiePublicPath("/lottie/kiss/mouth.json");

export const EGG_BROKEN_JSON = lottiePublicPath("/lottie/egg/egg-broken.json");

/** Tenor GIF (hosted under `public/gifs/`) — mixed into random poop throws with Lottie JSON. */
export const POOP_THROW_RUCKA_GIF = lottiePublicPath("/gifs/poop-rucka-ali.gif");

export function pickRandomThrowSrc(urls: readonly string[]): string {
  return urls[Math.floor(Math.random() * urls.length)]!;
}

/** Stable index for Strict Mode / dedupe keys (same string → same index). */
function hashDedupeToUint(dedupeId: string): number {
  let h = 2166136261;
  for (let i = 0; i < dedupeId.length; i++) {
    h = Math.imul(h ^ dedupeId.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/** One tomato scene URL per throw (from `public/lottie/tomato/*.json`), stable for `eventId`. */
export function pickStableTomatoThrowSrc(dedupeId: string): string {
  const pool = TOMATO_THROW_LOTTIE_SRCS;
  if (pool.length === 0) return TOMATO_THROW_JSON;
  return pool[hashDedupeToUint(dedupeId) % pool.length]!;
}

/** `Tomato.json` fills the viewport canvas more than `tomato-throw.json`; scale it down in the triple-throw player. */
export function tomatoThrowDisplayScale(src: string): number {
  return src.includes("Tomato.json") ? 0.68 : 1;
}

/** @deprecated Use WET_PAINT_SPLAT_JSON — kept for any external imports */
export const PAINT_SPLATTER_LOTTIE = WET_PAINT_SPLAT_JSON;

/** Wire IDs (≤32 chars for server validation); replaces arbitrary emoji payloads */
export const THROW_REACTION_IDS = {
  tomato: "throw_tomato",
  poop: "throw_poop",
  egg: "throw_egg",
  kiss: "throw_kiss",
  love: "throw_love",
  brokenHeart: "throw_broken_heart",
  punch: "throw_punch",
  highFive: "throw_high_five",
  dancing: "throw_dancing",
  angry: "throw_angry",
  facepalm: "throw_facepalm",
  slipper: "throw_slipper",
} as const;

export type ThrowSplatKind =
  | "tomato"
  | "poop"
  | "egg"
  | "kiss"
  | "love"
  | "brokenHeart"
  | "punch"
  | "highFive"
  | "dancing"
  | "angry"
  | "facepalm"
  | "slipper";

/** Resolved Lottie URL for one throw (random when multiple exist for that kind). */
export function pickRandomLottieForKind(kind: ThrowSplatKind): string {
  switch (kind) {
    case "tomato":
      return pickRandomThrowSrc(TOMATO_THROW_LOTTIE_SRCS);
    case "poop":
      return pickRandomThrowSrc(POOP_THROW_LOTTIE_SRCS);
    case "kiss":
      return pickRandomThrowSrc(KISS_THROW_LOTTIE_SRCS);
    case "love":
      return pickRandomThrowSrc(LOVE_THROW_LOTTIE_SRCS);
    case "brokenHeart":
      return pickRandomThrowSrc(BROKEN_HEART_THROW_LOTTIE_SRCS);
    case "egg":
      return pickRandomThrowSrc(EGG_THROW_LOTTIE_SRCS);
    case "punch":
      return pickRandomThrowSrc(PUNCH_THROW_LOTTIE_SRCS);
    case "highFive":
      return pickRandomThrowSrc(HIGH_FIVE_THROW_LOTTIE_SRCS);
    case "dancing":
      return pickRandomThrowSrc(DANCING_THROW_LOTTIE_SRCS);
    case "angry":
      return pickRandomThrowSrc(ANGRY_THROW_LOTTIE_SRCS);
    case "facepalm":
      return pickRandomThrowSrc(FACEPALM_THROW_LOTTIE_SRCS);
    case "slipper":
      return pickRandomThrowSrc(SLIPPER_THROW_LOTTIE_SRCS);
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export type ThrowVisual = { src: string; filter?: string };

const throwVisualByDedupeId = new Map<string, ThrowVisual>();

/**
 * Poop / egg / kiss: one random dedicated Lottie per throw (no wet-paint follow-up for poop).
 * Tomato uses `TomatoTripleThrowLottie` + `pickStableTomatoThrowSrc` — do not pass `"tomato"`. `dedupeId` must be stable
 * per throw for Strict Mode caching.
 */
export function getThrowVisual(
  kind: Exclude<ThrowSplatKind, "tomato">,
  dedupeId: string
): ThrowVisual {
  const cached = throwVisualByDedupeId.get(dedupeId);
  if (cached) return cached;

  const out: ThrowVisual =
    kind === "poop"
      ? {
          src: pickRandomThrowSrc([
            ...POOP_THROW_LOTTIE_SRCS,
            POOP_THROW_RUCKA_GIF,
          ]),
        }
      : { src: pickRandomLottieForKind(kind) };
  throwVisualByDedupeId.set(dedupeId, out);
  return out;
}

/** CSS filters on the Lottie wrapper. Tomato uses `tomato-throw.json` (designed colours) — no filter. */
export const SPLAT_FILTERS: Record<ThrowSplatKind, string> = {
  tomato: "none",
  poop: "none",
  egg: "none",
  kiss: "none",
  love: "none",
  brokenHeart: "none",
  punch: "none",
  highFive: "none",
  dancing: "none",
  angry: "none",
  facepalm: "none",
  slipper: "none",
};

export interface ThrowReactionMenuItem {
  id: string;
  label: string;
  emoji: string;
  color: string;
}

export const THROW_REACTION_MENU: ThrowReactionMenuItem[] = [
  { id: THROW_REACTION_IDS.tomato, label: "Tomato", emoji: "🍅", color: "#e8312a" },
  { id: THROW_REACTION_IDS.poop, label: "Poop", emoji: "💩", color: "#5C3317" },
  { id: THROW_REACTION_IDS.kiss, label: "Kiss", emoji: "💋", color: "#ec4899" },
  { id: THROW_REACTION_IDS.egg, label: "Egg", emoji: "🥚", color: "#f3d34a" },
  { id: THROW_REACTION_IDS.punch, label: "Punch", emoji: "👊", color: "#f59e0b" },
  { id: THROW_REACTION_IDS.angry, label: "Angry", emoji: "😠", color: "#dc2626" },
  { id: THROW_REACTION_IDS.facepalm, label: "Facepalm", emoji: "🤦", color: "#94a3b8" },
  { id: THROW_REACTION_IDS.slipper, label: "Slipper smack", emoji: "🩴", color: "#78350f" },
  { id: THROW_REACTION_IDS.love, label: "Love", emoji: "❤️", color: "#ef4444" },
  { id: THROW_REACTION_IDS.brokenHeart, label: "Broken heart", emoji: "💔", color: "#64748b" },
  { id: THROW_REACTION_IDS.dancing, label: "Dancing", emoji: "💃", color: "#a855f7" },
  { id: THROW_REACTION_IDS.highFive, label: "High five", emoji: "🙌", color: "#38bdf8" },
];

export function reactionTypeToThrowKind(reactionType: string): ThrowSplatKind | null {
  const t = typeof reactionType === "string" ? reactionType.trim() : "";
  switch (t) {
    case THROW_REACTION_IDS.tomato:
    case "🍅":
      return "tomato";
    case THROW_REACTION_IDS.poop:
    case "💩":
      return "poop";
    case THROW_REACTION_IDS.egg:
    case "🥚":
      return "egg";
    case THROW_REACTION_IDS.kiss:
    case "💋":
    case "😘":
      return "kiss";
    case THROW_REACTION_IDS.love:
    case "❤️":
    case "💕":
    case "🧸":
      return "love";
    case THROW_REACTION_IDS.brokenHeart:
    case "💔":
      return "brokenHeart";
    case THROW_REACTION_IDS.punch:
    case "👊":
      return "punch";
    case THROW_REACTION_IDS.angry:
    case "😠":
    case "😡":
      return "angry";
    case THROW_REACTION_IDS.facepalm:
    case "🤦":
    case "🤦‍♂️":
    case "🤦‍♀️":
      return "facepalm";
    case THROW_REACTION_IDS.slipper:
    case "🩴":
      return "slipper";
    case THROW_REACTION_IDS.highFive:
    case "🙌":
      return "highFive";
    case THROW_REACTION_IDS.dancing:
    case "💃":
    case "🕺":
      return "dancing";
    default:
      return null;
  }
}

/** Room toast / subtitle copy */
export function describeReactionForAnnounce(reactionType: string): string {
  switch (reactionType) {
    case THROW_REACTION_IDS.tomato:
    case "🍅":
      return "a tomato splat";
    case THROW_REACTION_IDS.poop:
    case "💩":
      return "a poop splat";
    case THROW_REACTION_IDS.egg:
    case "🥚":
      return "an egg splat";
    case THROW_REACTION_IDS.kiss:
    case "💋":
    case "😘":
      return "a kiss splat";
    case THROW_REACTION_IDS.love:
    case "❤️":
    case "💕":
    case "🧸":
      return "some love";
    case THROW_REACTION_IDS.brokenHeart:
    case "💔":
      return "a broken heart";
    case THROW_REACTION_IDS.punch:
    case "👊":
      return "a punch";
    case THROW_REACTION_IDS.angry:
    case "😠":
    case "😡":
      return "some rage";
    case THROW_REACTION_IDS.facepalm:
    case "🤦":
    case "🤦‍♂️":
    case "🤦‍♀️":
      return "a facepalm";
    case THROW_REACTION_IDS.slipper:
    case "🩴":
      return "a slipper smack";
    case THROW_REACTION_IDS.highFive:
    case "🙌":
      return "a high five";
    case THROW_REACTION_IDS.dancing:
    case "💃":
    case "🕺":
      return "a dance";
    default:
      return reactionType;
  }
}

/** Label shown on the overlay under the animation */
export function reactionTypeOverlayLabel(reactionType: string): string {
  const kind = reactionTypeToThrowKind(reactionType);
  if (!kind) return reactionType;
  const row = THROW_REACTION_MENU.find((m) => reactionTypeToThrowKind(m.id) === kind);
  return row ? `${row.emoji} ${row.label}` : reactionType;
}
